import { observer } from "mobx-react";
import Scaffold from "../../../../components/Scaffold";
import styles from "./index.module.scss";
import AdminClientStore from "../../../../stores/admin/adminClientStore";
import { useParams } from "react-router";
import { useEffect, useState } from "react";
import Utils from "../../../../utils/Utils";
import { Button, DatePicker, Form, Input, Modal, Select, InputNumber, Space, Alert, Tag, Result } from "antd";
import { Calendar2, PencilSquare, PlusCircle, List, Trash } from "react-bootstrap-icons";
import adminStore from "../../../../stores/admin/adminStore";
import AdminPendingPaymentsStore, { 
  CreatePendingPaymentData
} from "../../../../stores/admin/adminPendingPaymentsStore";
import appConfigStore from "../../../../stores/appConfigStore";
import AdminClientFeatures from "./AdminClientFeatures";
import dayjs from "dayjs";

const store = new AdminClientStore();
const paymentsStore = new AdminPendingPaymentsStore();

const AdminClientView = observer(() => {
  const { id } = useParams();
  const [form] = Form.useForm();
  const [paymentForm] = Form.useForm();
  const [selectedPayment, setSelectedPayment] = useState<any>(null);
  const [showCreatePaymentModal, setShowCreatePaymentModal] = useState(false);
  const [paymentStore] = useState(() => adminStore.pendingPayments);
  const isSuperAdmin = appConfigStore.isSuperAdmin;

  useEffect(() => {
    if (!id || !isSuperAdmin) return;
    store.findOne(id);
    paymentsStore.findAll({ userId: id });
    
    paymentStore.findLookups();
    
    return () => {
      store.item = undefined;
      paymentsStore.data = undefined;
    };
  }, [id, isSuperAdmin, paymentStore]);

  const onCreatePayment = async () => {
    try {
      const values = await paymentForm.validateFields();
      
      const paymentData: CreatePendingPaymentData = {
        userId: id!,
        tariffId: values.tariffId,
        licenseId: values.licenseId || undefined,
        amount: Number(values.amount),
        transactionId: values.transactionId || undefined,
        description: values.description || undefined,
        createdAt: values.createdAt ? dayjs(values.createdAt).toISOString() : undefined,
      };
      
      await paymentStore.onCreate(paymentData);
      
      setShowCreatePaymentModal(false);
      paymentForm.resetFields();
      
      paymentsStore.findAll({ userId: id });
      
    } catch (error) {
      console.error('Error creating payment:', error);
    }
  };

  const onDeletePayment = async (paymentId: string) => {
    Modal.confirm({
      title: "Удалить платеж?",
      content: "Вы уверены, что хотите удалить этот ручной платеж?",
      okText: "Удалить",
      cancelText: "Отмена",
      okType: "danger",
      async onOk() {
        try {
          await paymentStore.onDelete(paymentId);
          paymentsStore.findAll({ userId: id });
        } catch (error) {
          console.error('Error deleting payment:', error);
        }
      },
    });
  };

  const onConfirm = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();

      if (store.state === "Note") {
        await store.updateNote(values, id);
      }

      store.findOne(id);
    } catch (e) {
      console.error(e);
    }
  };

  const onEditNote = () => {
    form.setFieldsValue(store.item);
    store.state = "Note";
  };

  const onCancel = () => {
    form.resetFields();
    store.state = "None";
  };

  if (!isSuperAdmin) return <Result status="403" title="Нет доступа" />;
  if (!store.item || !id) return <div />;

  return (
    <Scaffold isBack title={`${store.item?.profile.firstName} - ${store.item?.email}`}>
      <div className={styles.area}>
        <AdminClientFeatures ownerId={id} />

        <div className={styles.column}>
          <div className={styles.header}>
            <div className={styles.title}>Профиль</div>
          </div>
          <div className={styles.profile}>
            <Label title="Имя" value={store.item.profile.firstName} />
            <Label title="Фамилия" value={store.item.profile.lastName} />
            <Label title="E-mail" value={store.item.email} />
          </div>
          
          <div className={styles.header} style={{ marginTop: 16 }}>
            <div className={styles.title}>Комментарии</div>
            <PencilSquare size={18} cursor={"pointer"} onClick={onEditNote} color="var(--primary)" />
          </div>
          <div className={styles.note}>{store.item.note}</div>
        </div>
          
        <div className={styles.column}>
          <div className={styles.header}>
            <div className={styles.title}>Платежи</div>
            <PlusCircle 
              size={18} 
              color="var(--primary)" 
              cursor={"pointer"} 
              onClick={() => setShowCreatePaymentModal(true)} 
            />
          </div>
          <div className={styles.transactions}>
            {paymentsStore.isLoading && <div>Загрузка...</div>}

            {!paymentsStore.isLoading && (!paymentsStore.data?.data || paymentsStore.data.data.length === 0) && (
              <div className={styles.empty}>Нет платежей</div>
            )}

            {!paymentsStore.isLoading && (paymentsStore?.data?.data?.length || 0) > 0 && (
              <div className={styles.paymentsList}>
                {paymentsStore?.data?.data.map((p) => (
                  <div key={p.id} className={styles.payment}>
                    <div className={styles.paymentRow}>
                      <div className={styles.paymentTitle}>
                        {p.tariff?.name || "Без тарифа"}
                        {p.isManual && (
                          <Tag color="orange" style={{ marginLeft: 8, fontSize: 11 }}>
                            Ручной
                          </Tag>
                        )}
                      </div>
                      <div className={styles.paymentAmount}>
                        {p.amount} {p.currency}
                      </div>
                    </div>

                    <div className={styles.paymentMeta}>
                      <span>{Utils.format(p.createdAt, "DD.MM.YYYY - HH:mm")}</span>
                      <span className={`${styles.status} ${styles[p.status.value]}`}>
                        {p.status.title}
                      </span>
                    </div>

                    {p.description && <div className={styles.description}>{p.description}</div>}

                    <div className={styles.paymentFooter}>
                      <Space>
                        {p.isManual && (
                          <Button
                            type="text"
                            danger
                            icon={<Trash size={18} />}
                            onClick={() => onDeletePayment(p.id)}
                          />
                        )}
                        <Button
                          type="text"
                          icon={<List size={18} />}
                          onClick={() => setSelectedPayment(p)}
                          style={{ color: "var(--night600)" }}
                        />
                      </Space>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      <Modal
        title="Редактировать"
        open={store.state !== "None"}
        okText="Сохранить"
        cancelText="Отменить"
        onOk={onConfirm}
        onClose={onCancel}
        okButtonProps={{ disabled: store.isLoading }}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Комментария" name="note">
            <Input.TextArea autoSize maxLength={10000} showCount />
          </Form.Item>
        </Form>
      </Modal>
      
      <Modal
        title="Детали платежа"
        open={!!selectedPayment}
        onCancel={() => setSelectedPayment(null)}
        footer={null}
        width={700}
      >
        {selectedPayment ? (
          <div className={styles.metadata}>
            {Object.entries(selectedPayment.metadata || {}).map(([key, value]) => (
              <div key={key} className={styles.metaRow}>
                <div className={styles.metaKey}>{key}</div>
                <div className={styles.metaValue}>
                  {typeof value === "object" ? JSON.stringify(value, null, 2) : String(value)}
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </Modal>
      
      <Modal
        title="Создать ручной платеж"
        open={showCreatePaymentModal}
        onOk={onCreatePayment}
        onCancel={() => {
          setShowCreatePaymentModal(false);
          paymentForm.resetFields();
        }}
        okText="Создать"
        cancelText="Отмена"
        okButtonProps={{ disabled: paymentStore.isLoading }}
        width={600}
      >     
        <Alert
          message="Ручной платеж"
          description="Этот платеж будет создан вручную и помечен соответствующим образом. Платеж будет автоматически в статусе 'Оплачено'."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form form={paymentForm} layout="vertical">
          <Form.Item
            name="licenseId"
            label="Привязать к лицензии"
          >
            <Select
              placeholder="Выберите лицензию (опционально)"
              allowClear
              options={store.item?.licenses.map((license) => ({
                label: `${license.tariff.name} (${Utils.format(license.expiresAt, 'DD.MM.YYYY')})`,
                value: license.id,
              }))}
            />
          </Form.Item>
            
          <Form.Item
            name="tariffId"
            label="Тариф"
            rules={[{ required: true, message: 'Выберите тариф' }]}
          >
            <Select
              placeholder="Выберите тариф"
              options={paymentStore.lookups?.tariffs?.map((tariff) => ({
                label: tariff.name,
                value: tariff.id,
              }))}
              loading={!paymentStore.lookups}
            />
          </Form.Item>
            
          <Form.Item
            name="amount"
            label="Сумма (₽)"
            rules={[
              { required: true, message: 'Введите сумму' },
              { type: 'number', min: 0.01, message: 'Сумма должна быть больше 0' },
            ]}
          >
            <InputNumber<number>
              placeholder="Введите сумму"
              style={{ width: '100%' }}
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
              parser={(value) => Number(value?.replace(/\s/g, '').replace(',', '.') ?? 0)}
              step={100}
              min={0}
            />
          </Form.Item>
          
          <Form.Item
            name="transactionId"
            label="ID транзакции"
          >
            <Input placeholder="Введите ID транзакции платежной системы" />
          </Form.Item>
          
          <Form.Item
            name="description"
            label="Описание платежа"
          >
            <Input.TextArea 
              placeholder="Например: 'Оплата тарифа PRO на 1 месяц'" 
              rows={3} 
            />
          </Form.Item>
          
          <Form.Item
            name="createdAt"
            label="Дата платежа"
            getValueProps={(value) => ({
              value: value ? dayjs(value) : undefined
            })}
          >
            <DatePicker
              style={{ width: '100%' }}
              format="DD.MM.YYYY HH:mm"
              showTime={{ format: 'HH:mm' }}
              placeholder="Выберите дату и время"
              suffixIcon={<Calendar2 size={18} />}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Scaffold>
  );
});

export default AdminClientView;

function Label({ title, value }: { title: string; value: string }) {
  return (
    <div className={styles.label}>
      <div>{`${title}:`}</div>
      <div>{value}</div>
    </div>
  );
}
